import React from 'react'
import classNames from 'classnames'
import { withRouter, RouteComponentProps } from 'react-router'
import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core/styles'
import { fade, darken } from '@material-ui/core/styles/colorManipulator'
import OriginalButton, { ButtonProps as OriginalButtonProps } from '@material-ui/core/Button'
import { Omit } from 'types'
import { NavLink, LinkProps } from 'react-router-dom'

export interface Props extends Omit<OriginalButtonProps, 'color' | 'classes'> {
  rounded?: boolean
  color?: OriginalButtonProps['color'] | 'dangerous' | 'accent' | 'facebook'
  to?: LinkProps['to']
  activeClassName?: string
}

const styles = (theme: Theme) => createStyles({
  notRounded: {
    borderRadius: 0,
  },
  dangerous: {
    color: theme.palette.error.main
  },
  containedDangerous: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: fade(theme.palette.error.main, .55)
    }
  },
  accent: {
    color: theme.palette.accent.main
  },
  containedAccent: {
    backgroundColor: theme.palette.accent.main,
    color: theme.palette.accent.contrastText,
    '&:hover': {
      backgroundColor: fade(theme.palette.accent.main, .55)
    }
  },
  facebook: {
    color: '#ffffff',
    backgroundColor: '#3c5a99',
    '&:hover': {
      backgroundColor: darken('#3c5a99', .2),
    },
  },
})

type InjectedProps = Props & RouteComponentProps<{}> & WithStyles<typeof styles>

const Button: React.SFC<InjectedProps> = ({
  classes,
  rounded,
  className,
  variant,
  color,
  to,
  history,
  location,
  match,
  staticContext,
  activeClassName,
  ...props
}) => {
  const isDangerous = color === 'dangerous'
  const isAccent = color === 'accent'
  const isFacebook = color === 'facebook'
  const isCustom = isDangerous || isAccent || isFacebook

  const component = React.useMemo(() => (
    to
      ? (
        linkProps => <NavLink {...linkProps} to={to} activeClassName={activeClassName} />
      ) as React.FC
      : 'button'
  ), [to, activeClassName])

  return (
    <OriginalButton
      {...props}
      color={isCustom ? undefined : (color as OriginalButtonProps['color'])}
      variant={variant}
      className={classNames(className, {
        [classes.notRounded]: !rounded,
        [classes.dangerous]: isDangerous,
        [classes.containedDangerous]: isDangerous && variant === 'contained',
        [classes.accent]: isAccent,
        [classes.containedAccent]: isAccent && variant === 'contained',
        [classes.facebook]: isFacebook,
      })}
      component={component}
    />
  )
}

Button.defaultProps = {
  rounded: true,
}

export default withStyles(styles)(withRouter(Button))
